import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { ArrowBack } from '@mui/icons-material'
import styled from '@emotion/styled'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import { Activity, ActivityRegistrationStatus, ActivityStatus, Client } from '../../../pure-js/types/GrooverTypes'
import { resolveActivityPageFieldVisibility } from '../../../pure-js/libs/clientHelper'
import Box from './Box'
import RegistrationInfo from './RegistrationInfo'
import RegistrationPrice from './RegistrationPrice'
import RegistrationOccurences from './RegistrationOccurences'
import RegistrationLocation from './RegistrationLocation'
import OtherActivititesTabs from './OtherActivitiesTabs'
import ActivitySuggestionList from './ActivitySuggestionList'
import GrooverText from './GrooverText'
import { getGrooverText } from '../libs/GrooverTextRepository'
import JsonModal from './JsonModal'
import { JsonIcon } from './Icons'
import PublicLanguageToggle from './PublicLanguageToggle'
import useAppState from '../hooks/useAppState'

import '../pages/ActivityDetailPage.css'

// Check if we're in development/localhost mode
const isDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

interface ActivityDetailContentProps {
  activity: Activity | null
  client: Client | null
  isLoading: boolean
  errorTextKey: string | null
  activityId?: string
  activityUrlPath?: string
}

export const ActivityDetailContent = ({
  activity,
  client,
  isLoading,
  errorTextKey,
  activityId,
  activityUrlPath
}: ActivityDetailContentProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { state } = useAppState()
  const language = state.selectedLanguage
  const isFromPublicPage = location.state?.from === 'PublicActivitiesPage'
  const activityPageVisibility = resolveActivityPageFieldVisibility(client ?? undefined)
  const showLocationSection =
    activityPageVisibility.showLocation ||
    activityPageVisibility.showRoom ||
    activityPageVisibility.showGoogleLinkAndMap

  // JSON Modal state
  const [jsonModalOpen, setJsonModalOpen] = useState(false)
  const [jsonModalData, setJsonModalData] = useState<unknown>(null)

  const isActivityValid = activity && activity.name

  const handleBack = () => {
    navigate(-1)
  }

  // Read iframeId from persisted config (set by PublicActivitiesPage on IFRAME_CONFIG message)
  const getIframeId = (): string => {
    const config = (window as unknown as Record<string, unknown>).__IFRAME_CONFIG as { iframeId?: string } | undefined
    return config?.iframeId || 'unknown'
  }

  // Consolidated height communication with parent iframe
  useEffect(() => {
    let lastSentHeight = 0
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const sendHeightToParent = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        const el = document.querySelector('[data-id="activity-details-page"]') as HTMLElement | null
        const height = el
          ? el.scrollHeight
          : Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)

        if (height === lastSentHeight) return
        lastSentHeight = height

        const iframeId = getIframeId()
        window.parent.postMessage({ type: 'adjustHeight', height, iframeId }, '*')
      }, 100)
    }

    sendHeightToParent()
    window.addEventListener('resize', sendHeightToParent)

    const contentEl = document.querySelector('[data-id="activity-details-page"]')
    const resizeObserver = new ResizeObserver(sendHeightToParent)
    if (contentEl) resizeObserver.observe(contentEl)
    resizeObserver.observe(document.body)

    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(sendHeightToParent)
    })
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    const messageHandler = (event: MessageEvent) => {
      if (!event.data) return
      const { type: msgType } = event.data
      if (msgType === 'requestHeightUpdate') {
        lastSentHeight = 0 // Force re-send
        sendHeightToParent()
      }
      // Also listen for IFRAME_CONFIG so late retry messages update the persisted config
      if (msgType === 'IFRAME_CONFIG') {
        const cfg = (window as unknown as Record<string, unknown>).__IFRAME_CONFIG as
          | Record<string, unknown>
          | undefined
        const current = cfg || {}
        ;(window as unknown as Record<string, unknown>).__IFRAME_CONFIG = {
          isMultipleIframes: event.data.isMultipleIframes ?? current.isMultipleIframes,
          forceNewTab: event.data.forceNewTab ?? current.forceNewTab,
          iframeId: event.data.iframeId ?? current.iframeId
        }
      }
    }
    window.addEventListener('message', messageHandler)

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      window.removeEventListener('resize', sendHeightToParent)
      window.removeEventListener('message', messageHandler)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  // Send IFRAME_NAVIGATED when content finishes loading (tells parent to scroll to iframe)
  useEffect(() => {
    if (isLoading) return

    const timer = setTimeout(() => {
      const el = document.querySelector('[data-id="activity-details-page"]') as HTMLElement | null
      const height = el ? el.scrollHeight : Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)

      const iframeId = getIframeId()
      window.parent.postMessage({ type: 'IFRAME_NAVIGATED', height, path: location.pathname, iframeId }, '*')
    }, 300)

    return () => clearTimeout(timer)
  }, [location.pathname, isLoading])

  return (
    <div className="master-container" data-id="activity-details-page">
      {isLoading ? (
        <div className="spinner">
          <GrooverText textKey="userWeb.activityDetail.loading" language={language} />
        </div>
      ) : errorTextKey ? (
        <div className="error-state">
          <p>
            <GrooverText textKey={errorTextKey} language={language} />
          </p>
          {errorTextKey === 'userWeb.activityDetail.error.activityNotFound' && client && (
            <ActivitySuggestionList client={client} />
          )}
        </div>
      ) : activity?.status === ActivityStatus.Cancelled ? (
        <p className="empty-state">
          <GrooverText textKey="userWeb.activityDetail.cancelledMessage" language={language} />
        </p>
      ) : activity?.status === ActivityStatus.Archived ? (
        <p className="empty-state">
          <GrooverText textKey="userWeb.activityDetail.archivedMessage" language={language} />
        </p>
      ) : isActivityValid ? (
        <>
          <NavContainer id="nav-container">
            {isFromPublicPage && (
              <BackButton onClick={handleBack} direction="row" align="center" gap="8px">
                <ArrowBack sx={{ fontSize: 20 }} />
                <GrooverText textKey="externalActivityPageNavigationReturnLabel" language={language} />
              </BackButton>
            )}
            <RightActions direction="row" align="center" gap="10px">
              <PublicLanguageToggle
                onLanguageChange={(language) => {
                  const next = new URLSearchParams(searchParams)
                  next.set('lang', language)
                  setSearchParams(next, { replace: true })
                }}
              />
              {/* JSON Viewer Button (development only) */}
              {isDev && (
                <Tooltip
                  title={getGrooverText('userWeb.activityDetail.debug.showJsonTooltip', language)}
                  arrow
                  enterDelay={300}
                >
                  <IconButton
                    onClick={() => {
                      setJsonModalData({
                        activity: activity,
                        client: client,
                        activityId: activityId,
                        activityUrlPath: activityUrlPath,
                        isFromPublicPage: isFromPublicPage,
                        location: {
                          pathname: location.pathname,
                          search: location.search,
                          hash: location.hash
                        }
                      })
                      setJsonModalOpen(true)
                    }}
                    sx={{
                      color: '#666',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <JsonIcon size={20} color="currentColor" />
                  </IconButton>
                </Tooltip>
              )}
            </RightActions>
          </NavContainer>
          {client?.importantAnnouncement && (
            <AnnouncementContainer fullWidth>
              <GrooverText textKey="externalActivityPageImportantAnnouncementLabel" language={language} />
              <Box style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: client.importantAnnouncement }} />
            </AnnouncementContainer>
          )}
          <Box direction="row" gap="16px" mobileColumn fullWidth>
            <LeftContainer gap="16px">
              <RegistrationInfo activity={activity} client={client || undefined} />
              {activity.registrationStatus !== ActivityRegistrationStatus.Special &&
                activity.eventType !== 'Parentevent' &&
                activity.eventType !== 'Bigevent' &&
                (activityPageVisibility.showPrice ||
                  (activityPageVisibility.showDropinPrice && !!activity.priceAtDoor)) && (
                  <RegistrationPrice activity={activity} client={client || undefined} />
                )}
            </LeftContainer>
            <RightContainer gap="16px" fullWidth>
              {activityPageVisibility.showInstructors && activity.instructorsNames && (
                <div className="segment-container">
                  <Box direction="column" gap="4px">
                    <Typography variant="body1">
                      <GrooverText
                        textKey="externalActivityPageSegmentInstructorHeader"
                        language={language}
                        style={{ fontWeight: 'bold' }}
                      />
                    </Typography>
                    <Typography variant="body1">{activity.instructorsNames}</Typography>
                  </Box>
                </div>
              )}
              {client && showLocationSection && (
                <RegistrationLocation activity={activity} customer={client} visibility={activityPageVisibility} />
              )}
              {activityPageVisibility.showOccasions && <RegistrationOccurences activity={activity} />}
              {client && activityPageVisibility.showOtherActivitiesAndEvents && (
                <OtherActivititesTabs activity={activity} client={client} />
              )}
            </RightContainer>
          </Box>
        </>
      ) : (
        <p className="empty-state">
          <GrooverText textKey="userWeb.activityDetail.emptyNoCourseOrEventFound" language={language} />
        </p>
      )}

      {/* JSON Modal */}
      <JsonModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={jsonModalData}
        title={getGrooverText('userWeb.activityDetail.debug.jsonModalTitle', language)}
      />
    </div>
  )
}

const AnnouncementContainer = styled(Box)`
  background-color: #fff3e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #ffe0b2;

  a {
    color: #e65100;
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
`

const LeftContainer = styled(Box)`
  flex: 0.6;
`

const RightContainer = styled(Box)`
  flex: 0.4;
`

const NavContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  margin-bottom: 8px;
`

const BackButton = styled(Box)`
  cursor: pointer;
  color: #333;
  padding: 8px;
  border-radius: 4px;
  width: fit-content;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  span {
    font-size: 14px;
  }
`

const RightActions = styled(Box)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`
